import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Grid, TextField, 
  FormControl, 
  FormLabel, RadioGroup, FormControlLabel, Radio, CircularProgress, Typography, 
  Alert, Stack, Card, CardContent, CardHeader, Chip, Accordion, AccordionSummary,
  AccordionDetails, Checkbox, Divider, IconButton, Tooltip, Badge, InputLabel, Select, MenuItem
} from '@mui/material';
import { 
  ExpandMore, Security, Person, CheckCircle, Warning, Info, Refresh,
  School, Assignment
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';
import useCurrentUser from '../../../utils/useCurrentUser';
import { ROLES } from '../../../config/pagePermissions';
import { roleGrantSummary, describeCataloguePath } from '../../../config/pageCatalogue.generated';

const UserForm = ({ 
  initialData, 
  roles,
  isEditing, 
  onSubmit, 
  onCancel,
  saving 
}) => {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  
  // Provide default values if initialData is null/undefined
  const defaultData = {
    name: '',
    email: '',
    phone: '',
    roleId: '',
    status: 'active'
  };
  
  const [formData, setFormData] = useState(initialData || defaultData);
  const [errors, setErrors] = useState({});
  const [passwordError, setPasswordError] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  // The same summary the Role Permissions dialog builds: granted pages grouped
  // by module, the always-available defaults, and any grant whose route the
  // catalogue does not list. Shared so the two surfaces cannot drift.
  // The active phase, so a secondary-only page is not listed as something this
  // role will give a nursery/primary user — see roleGrantSummary.
  const activePhaseForGrants = (
    localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || ''
  ).toString().trim().toLowerCase();
  const roleGrants = React.useMemo(
    () => roleGrantSummary(rolePermissions, { phaseCode: activePhaseForGrants }),
    [rolePermissions, activePhaseForGrants]
  );

  // Update formData when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      // Clear any previous errors when initialData changes
      if (initialData.error) {
        setSubmitStatus({ type: 'error', message: initialData.error });
        // Clear the error from initialData after displaying it
        delete initialData.error;
      }
    }
  }, [initialData]);

  // Load role permissions when role changes
  useEffect(() => {
    const loadRolePermissions = async () => {
      if (!formData.roleId) {
        setRolePermissions([]);
        setSelectedRole(null);
        return;
      }

      try {
        setLoadingPermissions(true);
        const role = roles.find(r => String(r.id) === String(formData.roleId));
        setSelectedRole(role);

        let permissions = [];
        if (role) {
          // Prefer permissions embedded in role from roles list
          if (role.permissions != null) {
            if (Array.isArray(role.permissions)) {
              permissions = role.permissions;
            } else if (typeof role.permissions === 'object') {
              permissions = Object.keys(role.permissions).filter(key => role.permissions[key] === true);
            }
          }
          // If not present, fetch from server fallback
          if (role.permissions == null && (!permissions || permissions.length === 0)) {
            try {
              const res = await fetchJson(`/api/users/roles/${role.id}/permissions`);
              const perms = Array.isArray(res?.permissions) ? res.permissions : [];
              permissions = perms;
            } catch (e) {
              console.warn('Failed to fetch role permissions from server:', e?.message || e);
            }
          }
        }

        setRolePermissions(permissions || []);
      } catch (error) {
        console.error('Failed to load role permissions:', error);
        setRolePermissions([]);
      } finally {
        setLoadingPermissions(false);
      }
    };
    
    loadRolePermissions();
  }, [formData.roleId, roles]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear submit status when user starts editing
    if (submitStatus.type) {
      setSubmitStatus({ type: '', message: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous status
    setSubmitStatus({ type: '', message: '' });
    
    // Basic validation
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = t('users.form.errors.nameRequired', { defaultValue: 'Name is required' });
    if (!formData.email?.trim()) newErrors.email = t('users.form.errors.emailRequired', { defaultValue: 'Email is required' });
    if (!formData.roleId) newErrors.roleId = t('users.form.errors.roleRequired', { defaultValue: 'Role is required' });
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Permissions are NOT sent. The role decides them.
    //
    // This used to submit customPermissions, which the role loader seeds to
    // { every role page: true }. userController.createUser turns that payload
    // into per-user PermissionOverride rows, so simply creating a user wrote an
    // explicit override for every page their role already granted. From then on
    // the user was pinned to that snapshot: editing the role in Role
    // Permissions changed nothing for them, because an override outranks the
    // role in checkUserPermission.
    //
    // With the checkboxes read-only there is nothing here to express anyway, so
    // the field is omitted and the user inherits the role — which is what
    // assigning a role is meant to mean. Deliberate per-user exceptions belong
    // in the permission overrides flow, not in the create-user form.
    const submissionData = { ...formData };

    try {
      const result = await onSubmit(submissionData);
      
      if (result && result.success) {
        setSubmitStatus({ type: 'success', message: result.message });
        // Clear form on success
        setTimeout(() => {
          setFormData(defaultData);
          setSelectedRole(null);
          setRolePermissions([]);
          setErrors({});
        }, 2000);
      } else if (result && !result.success) {
        setSubmitStatus({ type: 'error', message: result.message });
        
              // If it's an email conflict error, clear the email field and show helpful message
      if (result.message.includes('email') || result.message.includes('Email')) {
        setFormData(prev => ({ ...prev, email: '' }));
        setErrors(prev => ({ ...prev, email: t('users.form.errors.emailConflictShort', { defaultValue: 'Please use a different email address' }) }));
        setSubmitStatus({ 
          type: 'error', 
          message: t('users.form.errors.emailConflict', { defaultValue: 'This email is already registered. Please use a different email address and try again.' }) 
        });
      }
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: t('common.unexpectedError', { defaultValue: 'An unexpected error occurred. Please try again.' }) });
    }
  };

  // Calculate permission counts
  const rolePermissionsCount = roleGrants.granted.length;

  // Filter roles per business rules for the dropdown
  const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, '');
  const phaseNorm = (localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '').toString().trim().toLowerCase();
  const currentUserRole = norm(user?.role || user?.Role?.name);
  const systemAdminName = norm(ROLES?.SYSTEM_ADMIN || 'SystemAdmin');
  const schoolAdminName = norm(ROLES?.SCHOOL_ADMIN || 'SchoolAdmin');
  const forbidden = new Set([
    systemAdminName,
    'principalheadteacher', // legacy role must not appear
    'student'               // managed via admissions
  ]);
  if (currentUserRole !== 'schooladmin') {
    forbidden.add(schoolAdminName);
  }
  if (['principal','headmaster','principalheadteacher'].includes(currentUserRole)) {
    if (currentUserRole === 'principal') forbidden.add('headmaster');
    if (currentUserRole === 'headmaster') forbidden.add('principal');
  }
  const filteredRoles = Array.isArray(roles)
    ? roles
        .filter(r => !forbidden.has(norm(r?.name)))
        .filter(r => {
          const nm = norm(r?.name);
          if (phaseNorm === 'sec' && nm === 'headmaster') return false;
          if (phaseNorm === 'nur_pri' && nm === 'principal') return false;
          return true;
        })
    : [];

  const schoolRoles = filteredRoles.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {/* Status Messages */}
      {submitStatus.type && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Alert 
            severity={submitStatus.type === 'success' ? 'success' : 'error'}
            onClose={() => setSubmitStatus({ type: '', message: '' })}
          >
            {submitStatus.message}
          </Alert>
        </Grid>
      )}
      
      <Grid container spacing={3}>
        {/* Basic Information Card */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Person />
                  <Typography variant="h6">
                    {t('users.form.basicInfo') || 'Basic Information'}
                  </Typography>
                </Stack>
              }
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('users.form.fullName') || 'Full Name'}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('users.form.email') || 'Email'}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={async () => {
                      const v = (formData.email || '').trim();
                      if (!v) return;
                      try {
                        const params = new URLSearchParams();
                        params.set('email', v);
                        // Exclude current user when editing
                        if (formData && formData.id) params.set('excludeId', String(formData.id));
                        // Scope by school if available in storage
                        try {
                          const sid = localStorage.getItem('schoolId') || sessionStorage.getItem('schoolId');
                          if (sid) params.set('schoolId', String(sid));
                        } catch (_) {}
                        const res = await fetchJson(`/api/users/check-email?${params.toString()}`);
                        if (res && res.exists) {
                          setErrors(prev => ({ ...prev, email: t('users.form.errors.emailExistsInSchool', { defaultValue: 'This email is already registered in your school' }) }));
                        }
                      } catch (_) {}
                    }}
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                    disabled={false}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('users.form.phone') || 'Phone'}
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={async () => {
                      const v = (formData.phone || '').trim();
                      if (!v) return;
                      try {
                        const res = await fetchJson(`/api/users/check-phone?phone=${encodeURIComponent(v)}`);
                        if (res && res.exists) {
                          setErrors(prev => ({ ...prev, phone: t('users.form.errors.phoneExists', { defaultValue: 'This phone is already registered' }) }));
                        }
                      } catch (_) {}
                    }}
                    error={!!errors.phone}
                    helperText={errors.phone || ''}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={!!errors.roleId}>
                    <InputLabel>{t('users.form.role') || 'Role'}</InputLabel>
                    <Select
                      name="roleId"
                      value={formData.roleId || ''}
                      onChange={handleChange}
                      label={t('users.form.role') || 'Role'}
                    >
                      {schoolRoles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          <Box>
                            <Typography variant="body1">{role.name}</Typography>
                            {role.description && (
                              <Typography variant="caption" color="text.secondary">
                                {role.description}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.roleId && (
                      <Typography variant="caption" color="error">
                        {errors.roleId}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <Typography variant="subtitle1" gutterBottom>
                      {t('users.form.status') || 'Status'}
                    </Typography>
                    <RadioGroup
                      row
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <FormControlLabel 
                        value="active" 
                        control={<Radio />} 
                        label={t('users.statusActive') || 'Active'} 
                      />
                      <FormControlLabel 
                        value="inactive" 
                        control={<Radio />} 
                        label={t('users.statusInactive') || 'Inactive'} 
                      />
                      <FormControlLabel 
                        value="suspended" 
                        control={<Radio />} 
                        label={t('users.statusSuspended') || 'Suspended'} 
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Role Information Card */}
        {selectedRole && (
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <School />
                    <Typography variant="h6">
                      {t('users.form.roleInfo') || 'Role Information'}
                    </Typography>
                  </Stack>
                }
                action={
                  <Stack direction="row" spacing={1}>
                    <Chip
                      label={t('users.form.defaultPermissionsCount', {
                        count: rolePermissionsCount,
                        defaultValue: `${rolePermissionsCount} default permissions`,
                      })}
                      color="primary"
                      size="small"
                    />
                  </Stack>
                }
              />
              <CardContent>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>{selectedRole.name}</strong>: {selectedRole.description || t('users.form.roleDescriptionFallback', { defaultValue: 'No description available' })}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t('users.form.rolePermissionsHint', { defaultValue: 'Default permissions for this role are automatically selected. You can modify these permissions below.' })}
                  </Typography>
                </Alert>
                
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Permissions Management Card */}
        {selectedRole && (
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Security />
                    <Typography variant="h6">
                      {t('users.form.permissions') || 'Permissions Management'}
                    </Typography>
                  </Stack>
                }
              />
              <CardContent>
                {loadingPermissions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box>
                    {roleGrants.granted.length === 0 ? (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          {t('users.form.noDefaultPermissions', { defaultValue: 'This role has no default permissions configured.' })}
                        </Typography>
                      </Alert>
                    ) : (
                      <Box>
                        <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                          {t('users.form.rolePermissionsTitle', { count: rolePermissionsCount, defaultValue: 'Role Permissions ({{count}} total)' })}
                        </Typography>
                        {/* Read-only. What a role grants is decided in Role
                            Permissions, and editing it per user here made the
                            same question answerable in two places with no sign
                            of which had last won. These are shown so you can
                            see what the chosen role carries before assigning
                            it, not so they can be changed while doing so. */}
                        {/* Grouped by the same function the Role Permissions
                            dialog uses, so the two describe one role the same
                            way. A flat list here was the reason they appeared
                            to disagree. */}
                        {roleGrants.modules.map((mod) => (
                          <Box key={mod.module} sx={{ mb: 2 }}>
                            <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
                              {mod.module}
                            </Typography>
                            <Grid container spacing={1}>
                              {mod.pages.map((page) => (
                                <Grid item xs={12} md={6} key={page.path}>
                                  <FormControlLabel
                                    control={<Checkbox checked disabled color="primary" />}
                                    label={
                                      <Typography variant="body2" fontWeight="medium">
                                        {page.name}
                                      </Typography>
                                    }
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Every user holds these whatever their role. Leaving
                        them out made the list look short and suggested a role
                        could be denied its own profile. */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {t('users.form.defaultPages', { defaultValue: 'Given to everyone' })}
                      </Typography>
                      <Grid container spacing={1}>
                        {roleGrants.defaults.map((page) => (
                          <Grid item xs={12} md={6} key={page.path}>
                            <FormControlLabel
                              control={<Checkbox checked disabled color="default" />}
                              label={<Typography variant="body2">{page.name}</Typography>}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    {/* roleGrants.extra is deliberately not rendered: those are
                        routes the catalogue no longer lists — pages removed
                        from Role Permissions — and showing them here would
                        contradict the page that decides them. */}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        
        {/* Action Buttons */}
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button 
              variant="outlined" 
              onClick={onCancel}
              disabled={saving}
            >
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={saving || loadingPermissions}
              startIcon={
                saving ? <CircularProgress size={20} /> : <CheckCircle />
              }
            >
              {saving ? (
                t('common.saving') || 'Saving...'
              ) : isEditing ? (
                t('common.saveChanges') || 'Save Changes'
              ) : (
                t('users.createUser') || 'Create User'
              )}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserForm;